import {
  PersonalAccessTokenScopeValues,
  type IssuePersonalAccessTokenRequest,
  type PersonalAccessTokenScope,
} from "@features/personal-access-tokens/types";

const maxNameLength = 128;
const maxExpiryMilliseconds = 365 * 24 * 60 * 60 * 1000;

export interface IssueFormValues {
  readonly name: string;
  readonly scopes: readonly PersonalAccessTokenScope[];
  /** Пустой массив означает все текущие и будущие пространства владельца. */
  readonly workspaceIds: readonly string[];
  /** ISO-дата, обычно введённая в поле даты. */
  readonly expiresAt: string;
}

export type IssueFormField = "name" | "scopes" | "expiresAt";

export type IssueFormValidationError =
  "required" | "too-long" | "unknown-scope" | "invalid-date" | "not-in-future" | "beyond-maximum";

export type IssueFormErrors = Partial<Record<IssueFormField, IssueFormValidationError>>;

export class IssueFormValidationException extends Error {
  constructor(readonly errors: IssueFormErrors) {
    super("Форма выпуска персонального токена доступа заполнена неверно.");
    this.name = "IssueFormValidationException";
  }
}

/** Проверяет независимые от режима выпуска поля до отправки запроса. */
export const validateIssueForm = (
  form: IssueFormValues,
  now: Date = new Date(),
): IssueFormErrors => {
  const errors: IssueFormErrors = {};
  const normalizedName = form.name.trim();

  if (!normalizedName) {
    errors.name = "required";
  } else if (normalizedName.length > maxNameLength) {
    errors.name = "too-long";
  }

  if (form.scopes.length === 0) {
    errors.scopes = "required";
  } else if (form.scopes.some((scope) => !PersonalAccessTokenScopeValues.includes(scope))) {
    errors.scopes = "unknown-scope";
  }

  const expiresAt = new Date(form.expiresAt);
  const expiresAtMilliseconds = expiresAt.getTime();
  const nowMilliseconds = now.getTime();

  if (!Number.isFinite(expiresAtMilliseconds) || !Number.isFinite(nowMilliseconds)) {
    errors.expiresAt = "invalid-date";
  } else if (expiresAtMilliseconds <= nowMilliseconds) {
    errors.expiresAt = "not-in-future";
  } else if (expiresAtMilliseconds - nowMilliseconds > maxExpiryMilliseconds) {
    errors.expiresAt = "beyond-maximum";
  }

  return errors;
};

/** Преобразует чистую модель формы в тело уже проверенного запроса выпуска. */
export const buildIssueRequest = (
  form: IssueFormValues,
  idempotencyKey: string,
): IssuePersonalAccessTokenRequest => {
  const errors = validateIssueForm(form);
  if (Object.keys(errors).length > 0) throw new IssueFormValidationException(errors);

  return {
    name: form.name.trim(),
    scopes: [...form.scopes],
    workspaceIds: [...form.workspaceIds],
    expiresAt: new Date(form.expiresAt).toISOString(),
    idempotencyKey,
  };
};
